import chroma from "chroma-js";
import { castArray, mapValues, uniq } from "lodash";
import Highcharts from "./customHighCharts";

// Pattern hatching can make data labels hard to read, so they get a white outline, which
// hugs the glyphs instead of covering the pattern like a background box would. A thicker
// outline fills up the glyphs, and a larger text overflows the thin bars of the absolute
// values chart, so both values are at their maximum here.
const READABLE_DATA_LABEL_STYLE = {
  color: "#000000",
  fontSize: "0.9em",
  textOutline: "3px #ffffff"
};

const pointValue = point => {
  return typeof point === "number" ? point : point.y;
};

const seriesHasVisibleData = series => {
  return series.data.some(point => pointValue(point) > 0);
};

// The themes chart shades a theme color to tell its sub-themes apart, so points are
// grouped by the color they are shaded from rather than by their final color.
const baseColor = color => {
  return chroma.valid(color) ? chroma(color).hex("rgb") : color;
};

// Charts either take their colors from the chart palette or set one on every point, as
// the themes chart does.
const coloredPointsOf = chartOptions => {
  return (chartOptions.series || []).flatMap(series => {
    return (series.data || []).filter(point => point?.color && pointValue(point) > 0);
  });
};

export const chartHasMultipleColors = chartOptions => {
  const series = chartOptions.series || [];
  const coloredPoints = coloredPointsOf(chartOptions);

  if (coloredPoints.length > 0) {
    return uniq(coloredPoints.map(point => baseColor(point.color))).length > 1;
  }

  if (chartOptions.chart.type === "pie") {
    const visiblePoints = series[0]?.data.filter(point => pointValue(point) > 0) || [];

    return visiblePoints.length > 1;
  }

  return series.filter(seriesHasVisibleData).length > 1;
};

const patternOf = (color, index) => {
  const patterns = Highcharts.patterns;

  return { pattern: { ...patterns[index % patterns.length], color } };
};

const patternFillColors = chartOptions => {
  const colors = chartOptions.colors || Highcharts.getOptions().colors;

  return colors.map(patternOf);
};

// Points shaded from the same color share a pattern, so the sub-themes of a theme stay
// told apart by their shade, as they are without pattern fill.
const patternFillSeries = series => {
  const baseColors = uniq(
    series.flatMap(({ data }) => {
      return (data || [])
        .filter(point => point?.color)
        .map(point => baseColor(point.color));
    })
  );

  return series.map(singleSeries => ({
    ...singleSeries,
    data: (singleSeries.data || []).map(point => {
      if (!point?.color) return point;

      const index = baseColors.indexOf(baseColor(point.color));

      return { ...point, color: patternOf(point.color, index) };
    })
  }));
};

// Pie names are drawn away from the slices, on the chart background, so they stay
// readable as they are. Every other label is drawn on its own shape, over the pattern.
const drawnOverPattern = (chartType, dataLabel) => {
  return chartType !== "pie" || parseFloat(dataLabel.distance) < 0;
};

const readableDataLabels = plotOptions => {
  return mapValues(plotOptions, (typeOptions, chartType) => {
    if (!typeOptions.dataLabels) return typeOptions;

    const dataLabels = castArray(typeOptions.dataLabels).map(dataLabel => {
      if (!drawnOverPattern(chartType, dataLabel)) return dataLabel;

      return {
        ...dataLabel,
        style: { ...dataLabel.style, ...READABLE_DATA_LABEL_STYLE }
      };
    });

    return { ...typeOptions, dataLabels };
  });
};

// Unlike the export button, which the browser draws as a menu icon, this one only holds
// text, so it gets a border to be recognized as a control.
const PATTERN_FILL_BUTTON_THEME = {
  fill: "#ffffff",
  padding: 6,
  r: 3,
  stroke: "#767676",
  "stroke-width": 1,
  states: {
    hover: { fill: "#f0f0f0" },
    select: { fill: "#ffffff" }
  }
};

// Keep the Highcharts export button discreet in the top-right corner, away from
// the bottom legend. `x: 0` aligns it with the right edge; otherwise, Highcharts
// leaves space for other export buttons.
const patternFillButton = ({ enabled, labels, onToggle }) => ({
  text: enabled ? labels.disable : labels.enable,
  onclick: onToggle,
  align: "right",
  verticalAlign: "top",
  x: 0,
  theme: PATTERN_FILL_BUTTON_THEME
});

// Reserve space above the plot to prevent the toggle button from overlapping
// the chart content, such as the top bar of a full-width stacked chart.
const BUTTON_TOP_MARGIN = 40;

// Add the pattern fill toggle to charts with multiple colors and apply pattern
// fills to the chart colors when enabled.
export const withPatternFill = (chartOptions, patternFill) => {
  if (!chartHasMultipleColors(chartOptions)) return chartOptions;

  const { exporting = {} } = chartOptions;
  const buttons = {
    ...exporting.buttons,
    patternFillButton: patternFillButton(patternFill)
  };
  const options = {
    ...chartOptions,
    chart: { ...chartOptions.chart, marginTop: BUTTON_TOP_MARGIN },
    exporting: { ...exporting, buttons }
  };

  if (!patternFill.enabled) return options;

  return {
    ...options,
    colors: patternFillColors(chartOptions),
    plotOptions: readableDataLabels(chartOptions.plotOptions),
    series: patternFillSeries(chartOptions.series || [])
  };
};
